import {ScaleLinear} from 'd3';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {PolymerLayout, SliceMapType} from 'constants/MapsConstants';
import PartsView, {TOP_LINE_HEIGHT} from 'molbio/maps/linear/PartsView';
import {PART_NAME_FONT_SIZE} from 'molbio/maps/PartsViewConstants';
import {
  splitProteinPartModelsIntoHighlightRegions,
  PartHighlightRegion,
} from 'molbio/maps/splitPartsIntoHighlightRegions';
import Type from 'molbio/maps/Type';
import Protein from 'molbio/proteins/Protein';

// Number of pixels to show part background above and below the residues
const PROTEIN_PART_RESIDUE_MARGIN = 6;

function getPartRegionsForSlice(start: number, end: number, protein: Protein): Array<PartHighlightRegion> {
  const proteinLength = protein.length();
  // Sometimes, we have extra slices past the end of the sequence. This causes warnings when getting the
  // relevant parts, so we early return in those cases, and clamp the end such that start/end is always in
  // the [0, seqLength] range.
  if (start > proteinLength) {
    // Temporary assertion to determine whether this actually still happens, and can be removed if not
    assertAndContinue(false, 'Encountered slices past the end of the protein');
    return [];
  }
  const clampedEnd = Math.min(end, proteinLength);
  const parts = protein.getPartsBetween(start, clampedEnd);
  return splitProteinPartModelsIntoHighlightRegions(parts, proteinLength);
}

interface ProteinPartsViewProps {
  /** A function that takes the index of a residue and returns its x-axis position on the slice */
  xFn: ScaleLinear<number, number>;
  /** The index at which the slice starts */
  sliceStart: number;
  /** The index at which the slice ends */
  sliceEnd: number;
  /** The Backbone sequence being rendered */
  protein: Protein;
  /** Flat object layout data */
  polymerLayout: PolymerLayout;
  /** Height in pixels of the AA residues at default zoom. */
  aaHeight: number;
  /** Height in pixels of the space below the AA residues at default zoom. */
  aaSpacer: number;
  /**
   * A function that accepts a new box height or a way to calculate it and triggers a redraw with the new box
   * height.
   */
  redraw: (newBoxHeight?: number | ((layout: PolymerLayout) => number)) => void;
}

/**
 * Draws the shaded background over the residues of an AA sequence representing an AA part.
 */
export default function ProteinPartsView({
  sliceStart,
  sliceEnd,
  protein,
  aaHeight,
  aaSpacer,
  xFn,
  polymerLayout,
  redraw,
}: ProteinPartsViewProps): JSX.Element {
  const partRegions = getPartRegionsForSlice(sliceStart, sliceEnd, protein);
  const type = new Type(PART_NAME_FONT_SIZE);
  const boxHeight = TOP_LINE_HEIGHT + PROTEIN_PART_RESIDUE_MARGIN;
  const height = aaHeight + aaSpacer + PROTEIN_PART_RESIDUE_MARGIN;

  return (
    <PartsView
      sliceStart={sliceStart}
      sliceEnd={sliceEnd}
      partRegions={partRegions}
      useOverlappingPartsStyling={true}
      sequenceLength={protein.length()}
      boxHeight={boxHeight}
      height={height}
      type={type}
      backgroundY={0}
      xFn={xFn}
      polymerLayout={polymerLayout}
      mapType={SliceMapType.linear}
      redraw={redraw}
    />
  );
}
